/**
 * AgentTaskPanel
 * Manus-style task progress panel shown above the chat input while an agent is running.
 * Features:
 *  - Collapsed by default — shows current action label + step counter + loader
 *  - Click chevron to expand and see full task list
 *  - Clean single-stroke icons (no double circles)
 *  - No red X for errors — uses subtle amber/warning style instead
 *  - Progress bar in header
 */

import { useState, useEffect } from 'react';
import type { PlanningTask } from '@/hooks/useAgent';
import type { AgentStatus } from '@/hooks/useAgent';
import { SineThinkingLoader } from '@/components/ui/SineThinkingLoader';

interface AgentTaskPanelProps {
  planningTasks: PlanningTask[];
  currentActionLabel: string;
  startedAt: number | null;
  status: AgentStatus;
  currentTask: string;
}

function useElapsedTime(startedAt: number | null, active: boolean): string {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt || !active) return;
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt, active]);

  if (!startedAt) return '0:00';
  const total = active ? elapsed : Math.floor((Date.now() - startedAt) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Shimmer text for the active task
function ShimmerText({ text }: { text: string }) {
  return (
    <span className="agent-shimmer-text">{text}</span>
  );
}

function TaskIcon({ status }: { status: PlanningTask['status'] }) {
  if (status === 'done') {
    // Simple checkmark — no circle border
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="agent-task-icon agent-task-icon--done">
        <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (status === 'active') {
    return (
      <span className="agent-task-icon agent-task-icon--active">
        <span className="agent-task-dot" />
      </span>
    );
  }
  if (status === 'error') {
    // Neutral grey X — no warning/amber styling
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="agent-task-icon agent-task-icon--error">
        <path d="M5 5L11 11M11 5L5 11" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  // pending — simple small circle
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="agent-task-icon agent-task-icon--pending">
      <circle cx="8" cy="8" r="3" fill="#555"/>
    </svg>
  );
}

export function AgentTaskPanel({
  planningTasks,
  currentActionLabel,
  startedAt,
  status,
}: AgentTaskPanelProps) {
  const isActive = ['planning', 'running', 'waiting_approval', 'needs_takeover', 'takeover_active'].includes(status);
  const isCompleted = status === 'completed';
  // Collapsed by default — user clicks to expand
  const [expanded, setExpanded] = useState(false);
  useElapsedTime(startedAt, isActive);

  const doneCount = planningTasks.filter(t => t.status === 'done').length;
  const totalCount = planningTasks.length;
  const progressPct = totalCount > 0 ? (doneCount / totalCount) * 100 : 0;

  return (
    <div className={`agent-task-panel${expanded ? ' agent-task-panel--expanded' : ''}`}>
      {/* Header row — always visible */}
      <button
        className="agent-task-panel__header"
        onClick={() => setExpanded(e => !e)}
        type="button"
        aria-expanded={expanded}
      >
        <div className="agent-task-panel__header-left">
          {/* Loader spinner when active */}
          {isActive && (
            <SineThinkingLoader size="xs" showText={false} label="Aktiv" />
          )}
          <span className="agent-task-panel__action">
            {isCompleted ? 'Ferdig' : (currentActionLabel || 'Jobber...')}
          </span>
        </div>
        <div className="agent-task-panel__header-right">
          {/* Mini progress bar */}
          {totalCount > 0 && (
            <div className="agent-task-panel__progress-bar">
              <div
                className="agent-task-panel__progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          )}
          {totalCount > 0 && (
            <span className="agent-task-panel__progress">
              {doneCount}/{totalCount}
            </span>
          )}
          <svg
            width="14" height="14" viewBox="0 0 14 14" fill="none"
            className={`agent-task-panel__chevron${expanded ? ' agent-task-panel__chevron--up' : ''}`}
          >
            <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {/* Expanded task list */}
      {expanded && (
        <div className="agent-task-panel__body">
          {planningTasks.length === 0 ? (
            <div className="agent-task-panel__loading">
              <span className="agent-task-panel__loading-dot" />
              <span className="agent-task-panel__loading-dot" />
              <span className="agent-task-panel__loading-dot" />
            </div>
          ) : (
            <ul className="agent-task-panel__list">
              {planningTasks.map((task, i) => (
                <li
                  key={i}
                  className={`agent-task-panel__item agent-task-panel__item--${task.status}`}
                >
                  <TaskIcon status={task.status} />
                  {task.status === 'active' ? (
                    <ShimmerText text={task.label} />
                  ) : (
                    <span className="agent-task-panel__item-label">{task.label}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
